import { GroundTransportDateTime } from "components/AppointmentDatePicker/GroundTransportDateTime";
import { Checkbox } from "components/Atoms/inputs/Checkbox";
import { AppointmentPhaseDropdown } from "components/dropdowns/reactselect/AppointmentPhaseDropdown";
import GroundTransportTypeDropdown from "components/dropdowns/reactselect/GroundTransportTypeDropdown";
import { GoogleMapRoute } from "components/GoogleMap/GoogleMapRoute";
import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import ArtistModalPicker from "components/Picker/ArtistPicker/ArtistModalPicker";
import { ContactMultiModalPicker } from "components/Picker/ContactPicker/ContactMultiModalPicker";
import { GooglePlaceInputWithValue } from "components/ValidatedFields/GoolgePlaceInputWithValue";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import EmailInput from "components/ValidatedFields/WithErrorLabel/EmailInput";
import { GroundTransportDurationInput } from "groundTransport/GroundTransportDurationInput";
import { PickupLocationInput } from "groundTransport/PickupLocationInput";
import { PlanningContext } from "PlanningContext/PlanningContext";
import React, { useCallback, useEffect } from "react";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenGroundTransportSidePanel } from "./GroundSidePanel";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateGroundTransportsCache } from "utilities/api/groundTransportQuery";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import { GooglePlaceState } from "shared/utils/fieldsHelper/groundFields";
import { getCreateGroundTransportInstruction, getInitialNewGroundTransportFormState, NewGroundTransportFormState } from "shared/utils/fieldsHelper/newGroundFields";

const NewGroundSidePanel = () => {
  const planningContext = React.useContext(PlanningContext);
  const openGroundSidePanel = useOpenGroundTransportSidePanel();

  const [provideDriverInformation, setProvideDriverInformation] = React.useState(false);
  const [value, setValue] = React.useState<NewGroundTransportFormState>(
    getInitialNewGroundTransportFormState(serverReference, planningContext.opts?.artist, planningContext.opts?.date)
  );

  useEffect(() => {
    const fetchRoute = async () => {
      if (value.startPlace && value.endPlace) {
        const response = await window.CoreApi.GoogleDirections.FetchDirections({
          originGooglePlaceIdentifier: value.startPlace.placeIdentifier,
          destinationGooglePlaceIdentifier: value.endPlace.placeIdentifier
        });

        if (response.data.hasErrors) {
          return;
        }

        const directions = response.data.data;
        setValue(v => ({ ...v, directions }));

        if (directions && directions.durationEstimateInMinutes > 0) {
          setValue(v => ({ ...v, duration: directions.durationEstimateInMinutes }));
        }
      }
    }
    fetchRoute();
  }, [value.startPlace?.placeIdentifier, value.endPlace?.placeIdentifier]);

  useEffect(() => {
    const fetchTravellers = async () => {
      if (value.artist?.artistId) {
        const result = await window.CoreApi.CompanyToContact.ListContactByCompany({
          companyId: value.artist.artistId,
          defaultSelect: true,
          isActive: true,
          page: 1,
          perPage: 1000,
        });

        if (result.data.hasErrors) {
          return;
        }

        onChange({
          travellers: result.data.data.map(c => ({
            contactId: c.contact.contactId,
            culture: c.contact.culture,
            gender: c.contact.gender,
            isValidated: c.contact.isValidated,
            name: c.contact.name,
            position: c.contact.position,
            profileImageUrl: c.contact.profileImageUrl,
          }))
        });
      }
    }
    fetchTravellers();
  }, [value.artist?.artistId])

  const onDisplayDriverInfoChange = (checked: boolean) => {
    if (!checked) {
      setValue({
        ...value, driver: {
          name: "",
          mobile: "",
          email: "",
          vehicle: "",
        }
      });
    }
    setProvideDriverInformation(checked);
  }

  const onChange = (newData: Partial<NewGroundTransportFormState>) => {
    setValue({ ...value, ...newData });
  };

  const submit = () => {
    return window.CoreApi.GroundTransport.Create(getCreateGroundTransportInstruction(value, planningContext.opts?.fromShowId));
  }

  const queryClient = useQueryClient();

  return <SidePanelNewSectionTemplate
    afterCreate="displayItemInSidebar"
    onSave={id => {
      invalidateGroundTransportsCache(queryClient);
      openGroundSidePanel(id);
    }}
    title={RESX.GroundTransport.NewGroundTransport}
    id="new-ground-transport-side-panel"
    trigger={null}
    submit={submit}
    sections={[{
      key: "groundTransport",
      type: "regular",
      hideTitle: true,
      title: RESX.GroundTransport.resxGroundTransport,
      fields: [{
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.singular,
        render: () => <div className="main-section-template-fields-padding">
          <ArtistModalPicker
            securityScope="limitByUserAccess"
            canCreateNew={false}
            nonInPageEditing={{
              onChange: artist => onChange({ artist }),
              value: value.artist,
              validatorKey: "artist",
              required: true,
            }}
          />
        </div>
      }, {
        key: "travellers",
        label: RESX.Flight.Travellers,
        render: () => <div className="main-section-template-fields-padding">
          <ContactMultiModalPicker
            value={value.travellers}
            onChange={travellers => onChange({ travellers })}
            canCreateNew={false}
            required={false}
            validatorKey="contacts"
          />
        </div>
      }, {
        key: "status",
        label: RESX.Status.resxStatus,
        render: () => <AppointmentPhaseDropdown
          value={value.phase}
          onChange={phase => onChange({ phase })}
        />
      }, {
        key: "transport",
        label: RESX.GroundTransport.Transport,
        render: () => <GroundTransportTypeDropdown
          value={value.type}
          onChange={type => onChange({ type })}
        />
      }, {
        key: "notes",
        label: RESX.Note.Notes,
        render: () => <OptionalTextAreaInput
          maxLength={serverReference.constants.groundTransport.maxNotesLength}
          rows={3}
          value={value.notes}
          onChange={notes => onChange({ notes })}
        />
      }, {
        key: "displayDriverInfo",
        label: RESX.GroundTransport.Driver,
        render: () => <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
          <Checkbox
            checked={provideDriverInformation}
            onChange={() => onDisplayDriverInfoChange(!provideDriverInformation)} />
        </div>
      }, {
        key: "driverSectionHeader",
        label: "",
        hideLabel: true,
        hidden: !provideDriverInformation,
        render: () => <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
          <h3>{RESX.GroundTransport.Driver}</h3>
        </div>
      }, {
        key: "driverName",
        label: RESX.GroundTransport.DriverName,
        hidden: !provideDriverInformation,
        render: () => <OptionalTextInput
          focusOnMount
          maxLength={serverReference.constants.groundTransport.maxDriverNameLength}
          value={value.driver.name || ""}
          onChange={name => onChange({
            driver: {
              ...value.driver,
              name
            }
          })}
        />
      }, {
        key: "driverMobile",
        label: RESX.GroundTransport.DriverMobile,
        hidden: !provideDriverInformation,
        render: () => <OptionalTextInput
          maxLength={serverReference.constants.groundTransport.maxDriverMobileLength}
          value={value.driver.mobile || ""}
          onChange={mobile => onChange({
            driver: {
              ...value.driver,
              mobile
            }
          })}
        />
      }, {
        key: "driverEmail",
        label: RESX.GroundTransport.DriverEmail,
        hidden: !provideDriverInformation,
        render: () => <EmailInput
          maxLength={serverReference.constants.groundTransport.maxDriverEmailLength}
          value={value.driver.email || ""}
          onChange={email => onChange({
            driver: {
              ...value.driver,
              email
            }
          })}
          emailCanBeBlank
          validatorKey="driverEmail"
        />
      }, {
        key: "driverVehicleModel",
        label: RESX.GroundTransport.Vehicle,
        hidden: !provideDriverInformation,
        render: () => <OptionalTextInput
          maxLength={serverReference.constants.groundTransport.maxDriverVehicleLength}
          value={value.driver.vehicle || ""}
          onChange={vehicle => onChange({
            driver: {
              ...value.driver,
              vehicle
            }
          })}
        />
      }]
    }, {
      key: "fromTo",
      type: "regular",
      hideTitle: true,
      title: "",
      fields: [{
        key: "fromTo",
        label: "",
        hideLabel: true,
        render: () => <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
          <h3>{RESX.Flight.Departure} / {RESX.Flight.Arrival}</h3>
        </div>
      }, {
        key: "dateTime",
        label: RESX.DateTime.lblDate,
        render: () => <GroundTransportDateTime
          value={value.date}
          onChange={date => onChange({ date })}
        />
      }, {
        key: "duration",
        label: RESX.DateTime.Duration,
        render: () => <GroundTransportDurationInput
          value={value.duration}
          googleEstimatedDurationMinutes={value.directions?.durationEstimateInMinutes}
          onChange={duration => onChange({ duration })}
        />
      }, {
        key: "destination",
        label: RESX.GroundTransport.Destination,
        render: () => <div className="main-section-template-two-column-grid">
          <PickupLocationInput
            value={{
              pickupLocationType: value.startPickupLocationType,
              pickupLocationOther: value.startPickupLocationOther
            }}
            validatorKey="typeFrom"
            onChange={(v) => onChange({
              startPickupLocationType: v.pickupLocationType,
              startPickupLocationOther: v.pickupLocationOther
            })}
          />
          <PickupLocationInput
            value={{
              pickupLocationType: value.endPickupLocationType,
              pickupLocationOther: value.endPickupLocationOther
            }}
            validatorKey="typeTo"
            onChange={(v) => onChange({
              endPickupLocationType: v.pickupLocationType,
              endPickupLocationOther: v.pickupLocationOther
            })}
          />
        </div>
      }, {
        key: "address",
        label: RESX.Address.resxAddress,
        render: () => <div className="main-section-template-two-column-grid">
          <GooglePlaceInputWithValue
            validatorKey="start"
            calledFrom="ground"
            value={value.startPlace}
            onPlaceSelected={(v: GooglePlaceState) => {
              if (v === null && value.startPlace?.placeIdentifier && value.endPlace?.placeIdentifier) {
                onChange({ startPlace: v, directions: null, duration: 0 });
              } else {
                onChange({ startPlace: v, directions: null });
              }
            }}
          />
          <GooglePlaceInputWithValue
            validatorKey="end"
            calledFrom="ground"
            value={value.endPlace}
            onPlaceSelected={(v: GooglePlaceState) => {
              if (v === null && value.startPlace?.placeIdentifier && value.endPlace?.placeIdentifier) {
                onChange({ endPlace: v, directions: null, duration: 0 });
              } else {
                onChange({ endPlace: v, directions: null });
              }
            }}
          />
        </div>
      }, {
        key: "map",
        label: "",
        hidden: !value.startPlace?.geo && !value.endPlace?.geo,
        hideLabel: true,
        render: () => <div style={{ paddingTop: 8 }}>
          <GoogleMapRoute
            mapId="DEMO_MAP_ID"
            height={400}
            route={value.directions}
            startPlace={value.startPlace}
            endPlace={value.endPlace}
          />
        </div>
      }]
    }]}
  />
}

export const useOpenNewGroundSidePanel = () => {
  const openSidePanel = useOpenSidePanel();

  const openNewGroundSidePanel = useCallback(() => {
    openSidePanel(
      <NewGroundSidePanel />,
      "new-ground-transport-side-panel",
      "large"
    );
  }, [openSidePanel]);

  return openNewGroundSidePanel;
}